import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, Crown, Clock, Check, X, RefreshCw, 
  Swords, ArrowLeftRight, Eye, Loader2, Trophy, UserCheck, Coins
} from 'lucide-react';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

// Coin Toss Component
function CoinToss({ captainA, captainB, onResult, isActive }) {
  const [isFlipping, setIsFlipping] = useState(false);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const flipCoin = () => {
    if (isFlipping || !isActive) return;
    
    setIsFlipping(true);
    setShowResult(false);
    setResult(null);
    
    // Random result
    const winner = Math.random() < 0.5 ? 'captain_a' : 'captain_b';
    
    // Animate for 2 seconds then show result
    setTimeout(() => {
      setResult(winner);
      setShowResult(true);
      setIsFlipping(false);
    }, 2000);
  };

  const confirmResult = () => {
    if (result) {
      onResult(result);
    }
  };

  return (
    <div className="text-center space-y-6">
      {/* Coin */}
      <div className="relative w-32 h-32 mx-auto perspective-1000">
        <motion.div
          className="w-full h-full relative"
          style={{ transformStyle: 'preserve-3d' }}
          animate={isFlipping ? {
            rotateY: [0, 1800],
            rotateX: [0, 360, 720, 360, 0],
          } : {}}
          transition={{ duration: 2, ease: "easeInOut" }}
        >
          {/* Front - Captain A */}
          <div 
            className="absolute inset-0 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center shadow-lg border-4 border-red-300"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div className="text-center">
              <Crown className="w-8 h-8 text-white mx-auto mb-1" />
              <span className="text-xs font-bold text-white">A</span>
            </div>
          </div>
          {/* Back - Captain B */}
          <div 
            className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg border-4 border-blue-300"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div className="text-center">
              <Crown className="w-8 h-8 text-white mx-auto mb-1" />
              <span className="text-xs font-bold text-white">B</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Result Display */}
      <AnimatePresence>
        {showResult && result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="space-y-4"
          >
            <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${
              result === 'captain_a' 
                ? 'bg-red-500/20 border-2 border-red-500 text-red-400' 
                : 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
            }`}>
              <Trophy className="w-5 h-5" />
              <span className="font-bold text-lg">
                {result === 'captain_a' 
                  ? (captainA?.nickname || captainA?.name?.split(' ')[0])
                  : (captainB?.nickname || captainB?.name?.split(' ')[0])
                } picks first!
              </span>
            </div>
            <div>
              <Button 
                onClick={confirmResult}
                className="bg-green-500 hover:bg-green-600 text-white font-bold px-8"
              >
                <Check className="w-4 h-4 mr-2" /> Confirm & Start
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Flip Button */}
      {!showResult && (
        <Button
          onClick={flipCoin}
          disabled={isFlipping || !isActive}
          size="lg"
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-lg"
        >
          {isFlipping ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Flipping...
            </>
          ) : (
            <>
              <Coins className="w-5 h-5 mr-2" />
              Flip the Coin!
            </>
          )}
        </Button>
      )}

      {/* Manual Override */}
      {!isFlipping && !showResult && (
        <p className="text-white/40 text-sm mt-4">
          Or choose manually below
        </p>
      )}
    </div>
  );
}

function SelectionArena() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [arenaData, setArenaData] = useState(null);
  const [swaps, setSwaps] = useState([]);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [selectedGivePlayer, setSelectedGivePlayer] = useState(null);
  const [selectedWantPlayer, setSelectedWantPlayer] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchArenaData = useCallback(async () => {
    try {
      const [arenaRes, swapsRes] = await Promise.all([
        axios.get(`${API_URL}/api/matches/${matchId}/arena`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/matches/${matchId}/swaps`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }).catch(() => ({ data: { swaps: [] } }))
      ]);
      setArenaData(arenaRes.data);
      setSwaps(swapsRes.data.swaps || []);
    } catch (error) {
      console.error('Error fetching arena:', error);
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Arena not available');
      }
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchArenaData();
    // Poll every 3 seconds for real-time updates
    const interval = setInterval(fetchArenaData, 3000);
    return () => clearInterval(interval);
  }, [fetchArenaData]);

  const enterArena = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/arena/enter`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      toast.success('Entered the arena!');
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter arena');
    } finally {
      setActionLoading(false);
    }
  };

  const startArena = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/arena/start`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      toast.success('Arena started!');
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start arena');
    } finally {
      setActionLoading(false);
    }
  };

  const setFirstPicker = async (picker) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/arena/set-first-picker`, 
        { first_picker: picker },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
      toast.success(`${picker === 'captain_a' ? 'Team A' : 'Team B'} picks first!`);
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set first picker');
    } finally {
      setActionLoading(false);
    }
  };

  const pickPlayer = async (playerId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/matches/${matchId}/arena/pick`,
        { player_id: playerId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
      toast.success(res.data.message);
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick player');
    } finally {
      setActionLoading(false);
    }
  };

  const proposeSwap = async () => {
    if (!selectedGivePlayer || !selectedWantPlayer) {
      toast.error('Select both players for the swap');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/swaps/propose`,
        { give_player_id: selectedGivePlayer, want_player_id: selectedWantPlayer },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
      toast.success('Swap proposed!');
      setShowSwapDialog(false);
      setSelectedGivePlayer(null);
      setSelectedWantPlayer(null);
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to propose swap');
    } finally {
      setActionLoading(false);
    }
  };

  const respondToSwap = async (swapId, accept) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/swaps/${swapId}/respond`,
        { accept },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
      toast.success(accept ? 'Swap accepted!' : 'Swap rejected');
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to swap');
    } finally {
      setActionLoading(false);
    }
  };

  const finalizeTeams = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches/${matchId}/arena/finalize`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      toast.success('Teams finalized!');
      fetchArenaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize teams');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  if (!arenaData) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-lg mx-auto text-center py-12">
          <Swords className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Arena Not Available</h2>
          <p className="text-slate-600 mb-4">Captains haven't been assigned yet or this isn't a Sunday match.</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const { arena_state, captains, team_a, team_b, available_players, is_captain, user_role, can_pick, is_active, deadline } = arenaData;
  const status = arena_state?.status || 'not_started';
  
  // Get my team and other team for swap
  const myTeam = user_role === 'captain_a' ? team_a : team_b;
  const otherTeam = user_role === 'captain_a' ? team_b : team_a;
  
  // Check for pending swaps that I need to respond to
  const pendingSwapsForMe = swaps.filter(s => 
    s.status === 'pending' && 
    s.proposer_role !== user_role && 
    is_captain
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/dashboard')}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Swords className="w-5 h-5 text-[#E63946]" />
            <span className="font-bold">Selection Arena</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchArenaData} className="text-white/70 hover:text-white hover:bg-white/10">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        <div className="text-center">
          {!is_active && (
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-2">
              <Clock className="w-3 h-3 mr-1" /> Deadline Passed
            </Badge>
          )}
          <h1 className="text-2xl font-bold mb-1">Sunday Match Selection</h1>
          {deadline && (
            <p className="text-white/60 text-sm">
              Deadline: {new Date(deadline).toLocaleString('en-IN', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>

        {/* Captains Display */}
        <div className="grid grid-cols-2 gap-4">
          <Card className={`bg-gradient-to-br from-red-500/20 to-red-900/20 border-red-500/30 ${user_role === 'captain_a' ? 'ring-2 ring-red-500' : ''}`}>
            <CardContent className="p-4 text-center">
              <Crown className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-xs text-red-400 uppercase tracking-wider mb-1">Captain A</p>
              <p className="font-bold text-white">{captains?.captain_a?.nickname || captains?.captain_a?.name}</p>
              {arena_state?.captain_a_entered && (
                <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/30">
                  <Check className="w-3 h-3 mr-1" /> In Arena
                </Badge>
              )}
              {arena_state?.current_picker === 'captain_a' && status === 'in_progress' && (
                <Badge className="mt-2 bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse">
                  Picking...
                </Badge>
              )}
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br from-blue-500/20 to-blue-900/20 border-blue-500/30 ${user_role === 'captain_b' ? 'ring-2 ring-blue-500' : ''}`}>
            <CardContent className="p-4 text-center">
              <Crown className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">Captain B</p>
              <p className="font-bold text-white">{captains?.captain_b?.nickname || captains?.captain_b?.name}</p>
              {arena_state?.captain_b_entered && (
                <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/30">
                  <Check className="w-3 h-3 mr-1" /> In Arena
                </Badge>
              )}
              {arena_state?.current_picker === 'captain_b' && status === 'in_progress' && (
                <Badge className="mt-2 bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse">
                  Picking...
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Waiting Room / Entry State */}
        {(status === 'not_started' || status === 'waiting') && is_captain && is_active && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6 text-center">
              <Users className="w-12 h-12 text-[#E63946] mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Waiting Room</h3>
              {!arena_state?.captain_a_entered && !arena_state?.captain_b_entered ? (
                <p className="text-white/60 mb-4">No one has entered yet. Be the first!</p>
              ) : (
                <p className="text-white/60 mb-4">
                  {arena_state?.captain_a_entered && !arena_state?.captain_b_entered && 'Captain A is waiting...'}
                  {arena_state?.captain_b_entered && !arena_state?.captain_a_entered && 'Captain B is waiting...'}
                  {arena_state?.captain_a_entered && arena_state?.captain_b_entered && 'Both captains are ready!'}
                </p>
              )}
              <div className="flex gap-3 justify-center">
                {((user_role === 'captain_a' && !arena_state?.captain_a_entered) || 
                  (user_role === 'captain_b' && !arena_state?.captain_b_entered)) && (
                  <Button onClick={enterArena} disabled={actionLoading} className="bg-[#E63946] hover:bg-red-600">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enter Arena'}
                  </Button>
                )}
                {((user_role === 'captain_a' && arena_state?.captain_a_entered) || 
                  (user_role === 'captain_b' && arena_state?.captain_b_entered)) && (
                  <Button onClick={startArena} disabled={actionLoading} variant="outline" className="border-white/20 hover:bg-white/10">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Anyway'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Choose First Picker - with Coin Toss */}
        {status === 'choosing_first' && is_captain && is_active && (
          <Card className="bg-white/5 border-white/10 overflow-hidden">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">Who Picks First?</h3>
                <p className="text-white/60">Flip the coin or choose manually</p>
              </div>
              
              {/* Coin Toss Component */}
              <CoinToss 
                captainA={captains?.captain_a}
                captainB={captains?.captain_b}
                onResult={setFirstPicker}
                isActive={is_active && !actionLoading}
              />
              
              {/* Manual Selection */}
              <div className="flex gap-3 justify-center mt-6 pt-6 border-t border-white/10">
                <Button 
                  onClick={() => setFirstPicker('captain_a')} 
                  disabled={actionLoading}
                  variant="outline"
                  className="bg-red-500/10 hover:bg-red-500/30 border-red-500/30 text-red-400"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {captains?.captain_a?.nickname || captains?.captain_a?.name?.split(' ')[0]}
                </Button>
                <Button 
                  onClick={() => setFirstPicker('captain_b')} 
                  disabled={actionLoading}
                  variant="outline"
                  className="bg-blue-500/10 hover:bg-blue-500/30 border-blue-500/30 text-blue-400"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {captains?.captain_b?.nickname || captains?.captain_b?.name?.split(' ')[0]}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Spectator View - Coin Toss */}
        {status === 'choosing_first' && !is_captain && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6 text-center">
              <Coins className="w-12 h-12 text-amber-400 mx-auto mb-4 animate-bounce" />
              <h3 className="text-lg font-bold mb-2">Coin Toss in Progress!</h3>
              <p className="text-white/60">Captains are deciding who picks first...</p>
            </CardContent>
          </Card>
        )}

        {/* Spectator View */}
        {!is_captain && status !== 'completed' && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-4 text-center">
              <Eye className="w-8 h-8 text-white/40 mx-auto mb-2" />
              <p className="text-white/60">You're watching the selection live</p>
            </CardContent>
          </Card>
        )}

        {/* Main Selection Area */}
        {(status === 'in_progress' || status === 'picking_complete' || status === 'swapping' || status === 'completed') && (
          <div className="grid md:grid-cols-3 gap-4">
            {/* Team A */}
            <Card className="bg-gradient-to-b from-red-500/10 to-transparent border-red-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <Crown className="w-4 h-4" /> Team A ({team_a?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {team_a?.map((player, idx) => (
                  <div 
                    key={player?.user_id} 
                    className={`p-2 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 ${
                      idx === 0 ? 'ring-1 ring-red-500/50' : ''
                    } ${selectedGivePlayer === player?.user_id && user_role === 'captain_a' ? 'ring-2 ring-yellow-400' : ''}`}
                    onClick={() => {
                      if (showSwapDialog && user_role === 'captain_a' && player?.user_id !== captains?.captain_a?.user_id) {
                        setSelectedGivePlayer(player?.user_id);
                      }
                    }}
                  >
                    <span className="text-xs text-red-400/60 w-5">{idx + 1}</span>
                    <span className="flex-1 text-sm">{player?.nickname || player?.name}</span>
                    {idx === 0 && <Crown className="w-3 h-3 text-red-400" />}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Available Players */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-4 h-4" /> Available ({available_players?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {available_players?.length === 0 ? (
                  <p className="text-center text-white/40 py-4">All players picked!</p>
                ) : (
                  available_players?.map((player) => (
                    <div 
                      key={player?.user_id}
                      className={`p-2 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between ${
                        can_pick ? 'cursor-pointer hover:bg-white/10 hover:border-white/20' : ''
                      }`}
                      onClick={() => can_pick && pickPlayer(player?.user_id)}
                    >
                      <div>
                        <p className="text-sm font-medium">{player?.nickname || player?.name}</p>
                        <p className="text-xs text-white/40">{player?.cricket_role}</p>
                      </div>
                      {can_pick && (
                        <Button size="sm" variant="ghost" className="text-[#E63946]" disabled={actionLoading}>
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Team B */}
            <Card className="bg-gradient-to-b from-blue-500/10 to-transparent border-blue-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-blue-400 flex items-center gap-2">
                  <Crown className="w-4 h-4" /> Team B ({team_b?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {team_b?.map((player, idx) => (
                  <div 
                    key={player?.user_id} 
                    className={`p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center gap-2 ${
                      idx === 0 ? 'ring-1 ring-blue-500/50' : ''
                    } ${selectedWantPlayer === player?.user_id && user_role === 'captain_a' ? 'ring-2 ring-yellow-400' : ''}
                    ${selectedGivePlayer === player?.user_id && user_role === 'captain_b' ? 'ring-2 ring-yellow-400' : ''}`}
                    onClick={() => {
                      if (showSwapDialog) {
                        if (user_role === 'captain_a' && player?.user_id !== captains?.captain_b?.user_id) {
                          setSelectedWantPlayer(player?.user_id);
                        } else if (user_role === 'captain_b' && player?.user_id !== captains?.captain_b?.user_id) {
                          setSelectedGivePlayer(player?.user_id);
                        }
                      }
                    }}
                  >
                    <span className="text-xs text-blue-400/60 w-5">{idx + 1}</span>
                    <span className="flex-1 text-sm">{player?.nickname || player?.name}</span>
                    {idx === 0 && <Crown className="w-3 h-3 text-blue-400" />}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Swap Notifications */}
        {pendingSwapsForMe.length > 0 && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-yellow-400 flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4" /> Swap Proposals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingSwapsForMe.map((swap) => (
                <div key={swap.swap_id} className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm">
                      <span className="text-white/60">They give:</span>{' '}
                      <span className="font-medium">{swap.give_player?.nickname || swap.give_player?.name}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-white/60">They want:</span>{' '}
                      <span className="font-medium">{swap.want_player?.nickname || swap.want_player?.name}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => respondToSwap(swap.swap_id, true)}
                      disabled={actionLoading}
                      className="bg-green-500/20 hover:bg-green-500/40 text-green-400"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => respondToSwap(swap.swap_id, false)}
                      disabled={actionLoading}
                      variant="ghost"
                      className="text-red-400 hover:bg-red-500/20"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Actions for Captains after picking */}
        {is_captain && (status === 'picking_complete' || status === 'swapping') && is_active && (
          <div className="flex justify-center gap-3">
            <Button 
              onClick={() => setShowSwapDialog(true)}
              variant="outline"
              className="border-white/20 hover:bg-white/10"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Propose Swap
            </Button>
            <Button 
              onClick={finalizeTeams}
              disabled={actionLoading}
              className="bg-[#E63946] hover:bg-red-600"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finalize Teams'}
            </Button>
          </div>
        )}

        {/* Completed State */}
        {status === 'completed' && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-6 text-center">
              <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-green-400 mb-2">Teams Finalized!</h3>
              <p className="text-white/60">The selection is complete. Good luck to both teams!</p>
              {is_captain && is_active && (
                <Button 
                  onClick={() => setShowSwapDialog(true)}
                  variant="outline"
                  className="mt-4 border-white/20 hover:bg-white/10"
                >
                  <ArrowLeftRight className="w-4 h-4 mr-2" /> Propose Late Swap
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Swap History */}
        {swaps.length > 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white/80 text-sm flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4" /> Swap History
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {swaps.map((swap) => (
                <div key={swap.swap_id} className="p-2 bg-white/5 rounded text-sm flex items-center justify-between">
                  <span>
                    {swap.give_player?.nickname || swap.give_player?.name} ↔{' '}
                    {swap.want_player?.nickname || swap.want_player?.name}
                  </span>
                  <Badge className={
                    swap.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                    swap.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }>
                    {swap.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Swap Dialog */}
      <Dialog open={showSwapDialog} onOpenChange={setShowSwapDialog}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Propose a Swap</DialogTitle>
            <DialogDescription className="text-white/60">
              Select a player from your team to give, and a player from the other team to receive.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <p className="text-sm text-white/60 mb-2">You Give:</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {myTeam?.filter(p => p?.user_id !== (user_role === 'captain_a' ? captains?.captain_a?.user_id : captains?.captain_b?.user_id)).map((player) => (
                  <div
                    key={player?.user_id}
                    onClick={() => setSelectedGivePlayer(player?.user_id)}
                    className={`p-2 rounded cursor-pointer transition-colors ${
                      selectedGivePlayer === player?.user_id 
                        ? 'bg-red-500/30 border border-red-500' 
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    {player?.nickname || player?.name}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm text-white/60 mb-2">You Want:</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {otherTeam?.filter(p => p?.user_id !== (user_role === 'captain_a' ? captains?.captain_b?.user_id : captains?.captain_a?.user_id)).map((player) => (
                  <div
                    key={player?.user_id}
                    onClick={() => setSelectedWantPlayer(player?.user_id)}
                    className={`p-2 rounded cursor-pointer transition-colors ${
                      selectedWantPlayer === player?.user_id 
                        ? 'bg-green-500/30 border border-green-500' 
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    {player?.nickname || player?.name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSwapDialog(false)}>Cancel</Button>
            <Button 
              onClick={proposeSwap} 
              disabled={!selectedGivePlayer || !selectedWantPlayer || actionLoading}
              className="bg-[#E63946] hover:bg-red-600"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Propose Swap'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SelectionArena;
